// BI de flota (RentalOS F7) — el tablero del dueño.
//
// La métrica reina es utilización por unidad: dice qué fierro produce y cuál
// estorba en el patio. Con 74 unidades y 6.5% de utilización promedio, saber
// CUÁLES son las 62 ociosas es la diferencia entre comprar más equipo o mover
// el que ya se tiene.
//
// Nota sobre el ingreso: si sale $0 con equipo rentado, no es un bug del BI —
// es que las rentas no traen tarifa capturada (`rate_amount`). Se avisa en la
// UI para que no se lea como "no facturamos".

import { useEffect, useMemo, useState } from 'react';

import { fleetBI, fleetUnits } from '../api';
import type { FleetBI, UnitBI } from '../types';

const money = (n: number) =>
  `$${(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Kpi({ label, value, hint, tone }: { label: string; value: string; hint?: string; tone?: 'good' | 'warn' }) {
  const toneCls =
    tone === 'good' ? 'text-emerald-600 dark:text-emerald-400'
    : tone === 'warn' ? 'text-amber-600 dark:text-amber-400'
    : 'text-foreground';
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${toneCls}`}>{value}</div>
      {hint ? <div className="mt-1 text-xs text-muted-foreground">{hint}</div> : null}
    </div>
  );
}

export function FleetBIPage() {
  const [bi, setBi] = useState<FleetBI | null>(null);
  const [units, setUnits] = useState<UnitBI[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([fleetBI(), fleetUnits({ category: category || undefined })])
      .then(([b, u]) => {
        if (!alive) return;
        setBi(b);
        setUnits(Array.isArray(u) ? u : []);
        setError(null);
      })
      .catch((e) => alive && setError(e instanceof Error ? e.message : 'No se pudo cargar el BI'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [category]);

  const categories = useMemo(
    () => Object.keys(bi?.availability_by_category || {}).sort(),
    [bi],
  );

  // El ingreso en 0 con equipo afuera casi siempre significa tarifas sin
  // capturar, no que no se esté facturando. Vale distinguirlo en la UI.
  const rentedNoRate = useMemo(
    () => (units || []).filter((u) => u.rented_days > 0 && !u.revenue).length,
    [units],
  );

  if (loading) return <div className="p-4 text-sm text-muted-foreground">Cargando BI de flota…</div>;
  if (error) return <div className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">{error}</div>;
  if (!bi) return null;

  const f = bi.fleet;

  return (
    <div className="space-y-4">
      <div className="text-xs text-muted-foreground">
        Periodo: {bi.period.start} → {bi.period.end} ({bi.period.days} días)
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Unidades" value={String(f.units)} hint={`${f.idle_units.length} sin rentar en el periodo`} />
        <Kpi
          label="Utilización promedio"
          value={`${f.avg_utilization_pct}%`}
          tone={f.avg_utilization_pct < 30 ? 'warn' : 'good'}
          hint={f.avg_utilization_pct < 30 ? 'Flota subutilizada' : undefined}
        />
        <Kpi label="Ingreso del periodo" value={money(f.revenue)} tone={f.revenue > 0 ? 'good' : 'warn'} />
        <Kpi label="Neto (ingreso − servicio)" value={money(f.net)} hint={`Servicio: ${money(f.service_cost)}`} />
      </div>

      {rentedNoRate > 0 ? (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-foreground">
          <strong>{rentedNoRate}</strong> unidades rentadas no tienen tarifa capturada, por eso el ingreso sale en{' '}
          {money(f.revenue)}. No es un error del tablero: al capturar el precio en la renta, estas cifras se llenan solas.
        </div>
      ) : null}

      <div className="grid gap-3 lg:grid-cols-2">
        <div className="rounded-lg border border-border bg-card p-4">
          <h3 className="mb-3 text-sm font-semibold text-foreground">Disponibilidad por categoría</h3>
          <div className="space-y-1.5">
            {categories.map((c) => {
              const v = bi.availability_by_category[c];
              const none = v.available === 0;
              return (
                <div key={c} className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{c}</span>
                  <span className={none ? 'font-medium text-destructive' : 'text-foreground'}>
                    {v.available} libres / {v.total}
                    {v.out_of_service ? <span className="text-muted-foreground"> · {v.out_of_service} fuera</span> : null}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid gap-3">
          <div className="rounded-lg border border-border bg-card p-4">
            <h3 className="mb-2 text-sm font-semibold text-foreground">Cotizaciones</h3>
            <div className="text-sm text-muted-foreground">
              Ganadas <span className="font-medium text-foreground">{bi.quotes.won}</span> ·
              Perdidas <span className="font-medium text-foreground">{bi.quotes.lost}</span> ·
              Conversión <span className="font-medium text-foreground">{bi.quotes.conversion_pct}%</span>
            </div>
          </div>
          <div className="rounded-lg border border-border bg-card p-4">
            <h3 className="mb-2 text-sm font-semibold text-foreground">Cobranza</h3>
            <div className={`text-lg font-semibold ${bi.collections.overdue_balance > 0 ? 'text-destructive' : 'text-foreground'}`}>
              {money(bi.collections.overdue_balance)} <span className="text-xs font-normal text-muted-foreground">vencido</span>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h3 className="text-sm font-semibold text-foreground">Utilización y ROI por unidad</h3>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground"
          >
            <option value="">Todas las categorías</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-foreground">
              <tr className="border-b border-border">
                <th className="px-4 py-2">Equipo</th>
                <th className="px-4 py-2">Categoría</th>
                <th className="px-4 py-2 text-right">Días rentado</th>
                <th className="px-4 py-2 text-right">Utilización</th>
                <th className="px-4 py-2 text-right">Ingreso</th>
                <th className="px-4 py-2 text-right">Horómetro</th>
              </tr>
            </thead>
            <tbody>
              {(units || []).map((u) => (
                <tr key={u.asset_id} className="border-b border-border/60">
                  <td className="px-4 py-2">
                    <div className="font-medium text-foreground">{u.name}</div>
                    <div className="text-xs text-muted-foreground">{u.identifier}</div>
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">{u.category}</td>
                  <td className="px-4 py-2 text-right text-muted-foreground">{u.rented_days}/{u.period_days}</td>
                  <td className={`px-4 py-2 text-right font-medium ${u.utilization_pct === 0 ? 'text-destructive' : 'text-foreground'}`}>
                    {u.utilization_pct}%
                  </td>
                  <td className="px-4 py-2 text-right text-foreground">{money(u.revenue)}</td>
                  <td className="px-4 py-2 text-right text-muted-foreground">
                    {u.hour_meter ? `${u.hour_meter} h` : <span title="Sin lectura capturada">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(units || []).length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground">Sin unidades en el periodo.</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
